'use client';

import { type FooterData } from '@hen-site/types/layout';
import { buildPublicPath } from '@hen-site/utils/buildPublicPath';
import { Footer as FooterComponent, Link, Text } from '@highereducation/hen-ui';

type LegalFooterData = FooterData & {
  tenant?: string;
  disclosure?: string;
  /** Browser-facing host, resolved server-side, so links drop the tenant segment on the tenant's own host. */
  browserHost?: string;
};

type LegalFooterProps = {
  footerData?: LegalFooterData;
};

export const LegalFooter = ({ footerData = {} }: LegalFooterProps) => {
  const { name = '', tenant, disclosure = '', browserHost } = footerData;
  const currentYear = new Date().getFullYear();

  // Falls back to site-root paths when `tenant` is missing/empty so we never
  // emit `/undefined/...` or `//...`.
  const legalHref = (path: string) =>
    buildPublicPath(tenant ?? '', path, browserHost);

  const privacyLinks = [
    { href: legalHref('/privacy-policy/'), label: 'Privacy Policy' },
    {
      href: legalHref('/advertising-disclosure/'),
      label: 'Advertising Disclosure',
    },
    {
      href: legalHref('/do-not-sell-or-share-my-personal-information/'),
      label: 'Do Not Sell My Info',
    },
  ];

  // hen-ui `basic`: copyright/disclosure left, legal links right. No logo and
  // no `Footer.Bottom` (that subcomponent always draws a horizontal divider).
  return (
    <FooterComponent variant='basic'>
      <div className='flex w-full flex-wrap items-start justify-between gap-x-xl self-stretch'>
        <Text variant='b4' color='white'>
          {`© ${currentYear}${name ? ` ${name}` : ''}${
            disclosure ? `, ${disclosure}` : ''
          }`}
        </Text>
        <FooterComponent.Links>
          {privacyLinks.map(({ href, label }) => (
            <Link
              key={label}
              variant='white'
              href={href}
              target='_blank'
              rel='noopener noreferrer'
            >
              {label}
            </Link>
          ))}
        </FooterComponent.Links>
      </div>
    </FooterComponent>
  );
};
